import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MapPin, Phone, Clock, Euro, Check, X, Users, CreditCard, Hourglass, Search, Plus, Package, Tag } from "lucide-react";
import PushOptInBanner from "../components/PushOptInBanner";
import { motion, AnimatePresence } from "framer-motion";

function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function ymd(d) {
  // LOCAL YMD (non UTC) — fondamentale perché il backend genera slot per il giorno locale,
  // quindi se usiamo toISOString() su "25 apr 00:00 Europe/Rome" otteniamo "2026-04-24" (UTC)
  // e l'utente vede slot del giorno sbagliato.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function hm(iso) { const d = new Date(iso); return d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }); }

export default function BusinessDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [biz, setBiz] = useState(null);
  const [settings, setSettings] = useState({ combo_enabled: false, combo_staff_mode: "auto", services_layout: "auto" });
  const [packages, setPackages] = useState([]);
  // ----- Selection model -----
  // mode: "single" (default) | "combo" | "package"
  // single → selectedService is a Service object
  // combo  → comboServices is an ordered array of Service (≥1)
  // package → selectedPackage is the package object
  const [selectedService, setSelectedService] = useState(null);
  const [comboServices, setComboServices] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [comboStaffId, setComboStaffId] = useState(null); // when combo_staff_mode=customer_choice
  const [singleStaffId, setSingleStaffId] = useState(null); // when single service has ≥2 staff
  const [staff, setStaff] = useState([]); // active staff list (for combo customer-choice picker)
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [slots, setSlots] = useState([]);
  const [slotLoading, setSlotLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [msg, setMsg] = useState("");
  const [unavailable, setUnavailable] = useState(false);
  const [paymentState, setPaymentState] = useState(null); // null | 'polling' | 'success' | 'expired' | 'failed'
  const [privacyOk, setPrivacyOk] = useState(false);
  const [guestModalOpen, setGuestModalOpen] = useState(false);
  const [guestSuccess, setGuestSuccess] = useState(null);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [waitlistOk, setWaitlistOk] = useState(false);
  const [overlay, setOverlay] = useState({ holidays: [], overrides: [] });

  // Selection mode derived
  const selectionMode = selectedPackage ? "package" : (comboServices.length > 0 ? "combo" : (selectedService ? "single" : null));
  const hasSelection = !!selectionMode;

  // Aggregate selection details for booking display
  const selectionInfo = useMemo(() => {
    if (selectedPackage) {
      return {
        title: selectedPackage.name,
        services: selectedPackage.services || [],
        totalDuration: selectedPackage.total_duration_min,
        totalPrice: selectedPackage.final_price,
        hasDeposit: false,
      };
    }
    if (comboServices.length > 0) {
      const dur = comboServices.reduce((acc, s) => acc + (s.duration_min || 0) + (s.buffer_min || 0), 0);
      const price = comboServices.reduce((acc, s) => acc + Number(s.price || 0), 0);
      return {
        title: comboServices.map((s) => s.name).join(" + "),
        services: comboServices,
        totalDuration: dur,
        totalPrice: price,
        hasDeposit: false,
      };
    }
    if (selectedService) {
      return {
        title: selectedService.name,
        services: [selectedService],
        totalDuration: (selectedService.duration_min || 0) + (selectedService.buffer_min || 0),
        totalPrice: Number(selectedService.price || 0),
        hasDeposit: !!(selectedService.deposit_type && selectedService.deposit_type !== "none" && Number(selectedService.deposit_value) > 0),
      };
    }
    return null;
  }, [selectedService, comboServices, selectedPackage]);

  // Fetch the public calendar overlay so we can warn customers about holidays/special hours
  useEffect(() => {
    if (!id) return;
    api.get(`/businesses/${id}/calendar-overlay?days=120`)
      .then((r) => setOverlay(r.data || { holidays: [], overrides: [] }))
      .catch(() => setOverlay({ holidays: [], overrides: [] }));
  }, [id]);

  const selectedYmd = ymd(selectedDate);
  const selectedHoliday = useMemo(
    () => overlay.holidays.find((h) => h.date === selectedYmd) || null,
    [overlay.holidays, selectedYmd],
  );
  const selectedOverride = useMemo(() => {
    if (selectedHoliday) return null;
    const wk = (selectedDate.getDay() + 6) % 7;
    return overlay.overrides.find(
      (o) => selectedYmd >= o.date_from && selectedYmd <= o.date_to && (o.active_dows || []).includes(wk),
    ) || null;
  }, [overlay.overrides, selectedYmd, selectedDate, selectedHoliday]);

  // Next upcoming closure (used by the “heads-up” banner)
  const nextClosure = useMemo(() => {
    const todayY = ymd(new Date());
    const upcoming = overlay.holidays.filter((h) => h.date >= todayY).sort((a, b) => a.date.localeCompare(b.date));
    return upcoming[0] || null;
  }, [overlay.holidays]);

  // Handle Stripe Checkout return
  useEffect(() => {
    const sessionId = sp.get("session_id");
    const cancelled = sp.get("cancelled");
    if (cancelled) {
      setMsg("Pagamento annullato. La prenotazione non è stata confermata.");
      sp.delete("cancelled"); setSp(sp, { replace: true });
      return;
    }
    if (!sessionId) return;
    setPaymentState("polling");
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/v1/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setPaymentState("success");
          setMsg("Pagamento ricevuto! La tua prenotazione è confermata.");
          return;
        }
        if (data.status === "expired") {
          setPaymentState("expired");
          setMsg("Sessione di pagamento scaduta.");
          return;
        }
        if (attempts++ < 12) setTimeout(poll, 2500);
        else { setPaymentState("failed"); setMsg("Verifica pagamento scaduta. Controlla lo stato della tua prenotazione."); }
      } catch {
        setPaymentState("failed");
        setMsg("Errore nel recupero dello stato del pagamento.");
      }
    };
    poll();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setUnavailable(false);
    api.get(`/businesses/${id}`)
      .then((r) => setBiz(r.data))
      .catch((e) => {
        if (e.response?.status === 403) setUnavailable(true);
        else setMsg(formatErr(e.response?.data?.detail) || "Errore nel caricamento");
      });
    // Public settings (combo flag, layout)
    api.get(`/businesses/${id}/settings`).then((r) => setSettings(r.data || {})).catch(() => {});
    // Public active packages
    api.get(`/businesses/${id}/packages?only_active=true`).then((r) => setPackages(r.data || [])).catch(() => setPackages([]));
    // Active staff (used when combo_staff_mode=customer_choice)
    api.get(`/businesses/${id}/staff`).then((r) => setStaff((r.data || []).filter((s) => s.active !== false))).catch(() => setStaff([]));
  }, [id]);

  useEffect(() => {
    if (!hasSelection) return;
    setSlotLoading(true);
    const day = ymd(selectedDate);
    const onDone = (raw) => {
      const norm = raw.map((s) => (typeof s === "string" ? { start: s, suggested: false } : s));
      setSlots(norm);
      setSelectedSlot((prev) => (prev && norm.some((s) => s.start === prev) ? prev : null));
    };
    let req;
    if (selectionMode === "single") {
      const params = { service_id: selectedService.id, day };
      if (singleStaffId) params.staff_id = singleStaffId;
      req = api.get(`/businesses/${id}/availability`, { params });
    } else {
      // combo or package → use availability-combo with the underlying services
      const sids = (selectedPackage ? selectedPackage.service_ids : comboServices.map((s) => s.id)).join(",");
      const params = { service_ids: sids, day };
      if (settings.combo_staff_mode === "customer_choice" && comboStaffId) params.staff_id = comboStaffId;
      req = api.get(`/businesses/${id}/availability-combo`, { params });
    }
    req
      .then((r) => onDone(r.data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setSlotLoading(false));
  }, [selectionMode, selectedService, comboServices, selectedPackage, comboStaffId, singleStaffId, selectedDate, id, settings.combo_staff_mode, hasSelection]);

  const days = useMemo(() => {
    const base = new Date();
    base.setHours(0, 0, 0, 0);
    return Array.from({ length: 14 }, (_, i) => addDays(base, i));
  }, []);

  // Restore booking context after login-and-return
  useEffect(() => {
    if (!user || !biz) return;
    const raw = sessionStorage.getItem("pendingBooking");
    if (!raw) return;
    try {
      const p = JSON.parse(raw);
      if (p.bizId !== id) return;
      sessionStorage.removeItem("pendingBooking");
      const svc = biz.services?.find((s) => s.id === p.serviceId);
      if (svc) setSelectedService(svc);
      if (p.slot) {
        setSelectedDate(new Date(p.slot));
        setSelectedSlot(p.slot);
        setPrivacyOk(true);
      }
    } catch {
      sessionStorage.removeItem("pendingBooking");
    }
  }, [user, biz, id]);

  // Helper: clear selection (used when switching modes)
  function clearSelection() {
    setSelectedService(null);
    setComboServices([]);
    setSelectedPackage(null);
    setComboStaffId(null);
    setSingleStaffId(null);
    setSelectedSlot(null);
  }

  function chooseSingle(s) {
    setComboServices([]);
    setSelectedPackage(null);
    setComboStaffId(null);
    setSingleStaffId(null); // reset staff picker when switching service
    setSelectedService(s);
  }

  function choosePackage(p) {
    setSelectedService(null);
    setComboServices([]);
    setComboStaffId(null);
    setSelectedPackage(p);
  }

  function toggleComboService(s) {
    setSelectedService(null);
    setSelectedPackage(null);
    setComboServices((arr) => {
      const has = arr.find((x) => x.id === s.id);
      if (has) {
        return arr.filter((x) => x.id !== s.id);
      }
      if (arr.length >= 5) return arr;
      return [...arr, s];
    });
  }

  async function confirm() {
    if (!hasSelection || !selectedSlot) return;
    if (!privacyOk) { setMsg("Devi accettare l'Informativa Privacy per prenotare."); return; }

    // Combo & package handle deposit upstream (combo doesn't support deposit). Single still uses Stripe.
    if (selectionMode === "single" && selectionInfo.hasDeposit) {
      let customerName = user?.name;
      let customerEmail = user?.email;
      if (!customerName) {
        customerName = window.prompt("Il tuo nome completo:");
        if (!customerName) return;
      }
      if (!customerEmail) {
        customerEmail = window.prompt("La tua email (per ricevuta Stripe):");
        if (!customerEmail) return;
      }
      setConfirming(true);
      setMsg("");
      try {
        const { data } = await api.post("/bookings/deposit-intent", {
          business_id: id,
          service_id: selectedService.id,
          start_at: selectedSlot,
          customer_name: customerName,
          customer_email: customerEmail,
          origin_url: window.location.origin,
          privacy_accepted: true,
        });
        window.location.href = data.url;
      } catch (e) {
        setMsg(formatErr(e.response?.data?.detail) || "Errore");
        setConfirming(false);
      }
      return;
    }

    if (!user) {
      setGuestModalOpen(true);
      return;
    }
    setConfirming(true);
    setMsg("");
    try {
      if (selectionMode === "single") {
        await api.post("/bookings", {
          business_id: id,
          service_id: selectedService.id,
          start_at: selectedSlot,
          staff_id: singleStaffId || null,
          privacy_accepted: true,
        });
      } else if (selectionMode === "combo") {
        await api.post("/bookings/combo", {
          business_id: id,
          service_ids: comboServices.map((s) => s.id),
          start_at: selectedSlot,
          staff_id: comboStaffId || null,
          privacy_accepted: true,
        });
      } else if (selectionMode === "package") {
        await api.post(`/packages/${selectedPackage.id}/book`, {
          start_at: selectedSlot,
          staff_id: comboStaffId || null,
          privacy_accepted: true,
        });
      }
      setMsg("Prenotazione confermata! Puoi vederla nel tuo account.");
      setTimeout(() => nav("/me"), 1200);
    } catch (e) {
      setMsg(formatErr(e.response?.data?.detail) || "Errore");
    } finally {
      setConfirming(false);
    }
  }

  async function submitGuestBooking(guest) {
    if (!hasSelection || !selectedSlot) return;
    setConfirming(true);
    setMsg("");
    try {
      let data;
      if (selectionMode === "single") {
        const r = await api.post("/bookings/guest", {
          business_id: id,
          service_id: selectedService.id,
          start_at: selectedSlot,
          staff_id: singleStaffId || null,
          customer_name: guest.name,
          customer_email: guest.email,
          customer_phone: guest.phone,
          notes: guest.notes || "",
          privacy_accepted: true,
        });
        data = r.data;
      } else if (selectionMode === "combo") {
        const r = await api.post("/bookings/combo-guest", {
          business_id: id,
          service_ids: comboServices.map((s) => s.id),
          start_at: selectedSlot,
          staff_id: comboStaffId || null,
          customer_name: guest.name,
          customer_email: guest.email,
          customer_phone: guest.phone,
          notes: guest.notes || "",
          privacy_accepted: true,
        });
        data = { ...r.data, service_name: selectionInfo.title, start_at: r.data.start_at, id: r.data.combo_id };
      } else if (selectionMode === "package") {
        const r = await api.post(`/packages/${selectedPackage.id}/book-guest`, {
          start_at: selectedSlot,
          staff_id: comboStaffId || null,
          customer_name: guest.name,
          customer_email: guest.email,
          customer_phone: guest.phone,
          notes: guest.notes || "",
          privacy_accepted: true,
        });
        data = { ...r.data, service_name: r.data.package_name || selectionInfo.title, start_at: r.data.start_at, id: r.data.combo_id };
      }
      setGuestModalOpen(false);
      setGuestSuccess({
        bookingId: data.id,
        name: guest.name,
        email: guest.email,
        phone: guest.phone,
        serviceName: data.service_name,
        startAt: data.start_at,
      });
      setSelectedSlot(null);
      setPrivacyOk(false);
    } catch (e) {
      setMsg(formatErr(e.response?.data?.detail) || "Errore");
    } finally {
      setConfirming(false);
    }
  }

  if (unavailable) {
    return (
      <div className="max-w-md mx-auto px-4 py-20 text-center" data-testid="biz-unavailable">
        <div className="inline-flex h-14 w-14 rounded-2xl bg-zinc-900 text-white items-center justify-center mb-4">
          <X className="h-6 w-6" />
        </div>
        <h1 className="font-display text-2xl font-medium tracking-tight">Temporaneamente non disponibile</h1>
        <p className="text-zinc-500 mt-3 dark:text-zinc-400">Le prenotazioni online per questa attività sono al momento sospese. Contatta direttamente l'attività per informazioni.</p>
      </div>
    );
  }

  if (!biz) return <div className="p-10 text-sm text-zinc-500 dark:text-zinc-400">Caricamento…</div>;

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
      {paymentState && (
        <div className={`mb-5 rounded-2xl p-4 flex items-center gap-3 ${paymentState === "success" ? "bg-emerald-50 border border-emerald-200 dark:bg-emerald-950/40 dark:border-emerald-800/50" : paymentState === "polling" ? "bg-blue-50 border border-blue-200 dark:bg-blue-950/40 dark:border-blue-800/50" : "bg-amber-50 border border-amber-200 dark:bg-amber-950/40 dark:border-amber-800/50"}`} data-testid="payment-banner">
          <div className={`h-10 w-10 rounded-xl flex items-center justify-center text-white ${paymentState === "success" ? "bg-emerald-600" : paymentState === "polling" ? "bg-blue-600" : "bg-amber-600"}`}>
            {paymentState === "success" ? <Check className="h-5 w-5" /> : <CreditCard className="h-5 w-5" />}
          </div>
          <div className="flex-1 text-sm">
            <div className="font-medium">{paymentState === "success" ? "Prenotazione confermata" : paymentState === "polling" ? "Verifica del pagamento in corso…" : "Pagamento non completato"}</div>
            <div className="text-zinc-600 dark:text-zinc-400">{msg}</div>
          </div>
          {paymentState === "success" && <button onClick={() => nav("/me")} className="btn-accent h-10 px-4 text-sm">Vedi prenotazione</button>}
        </div>
      )}
      {/* Cover */}
      <div className="relative h-56 md:h-72 rounded-3xl overflow-hidden mb-6">
        {biz.cover_image && <img src={biz.cover_image} alt={biz.name} className="w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent" />
        <div className="absolute bottom-5 left-5 right-5 text-white">
          <div className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-300">{biz.category}</div>
          <h1 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-1">{biz.name}</h1>
          <div className="flex flex-wrap gap-4 mt-2 text-sm">
            <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" />{biz.city}{biz.address ? ` · ${biz.address}` : ""}</span>
            {biz.phone && <span className="inline-flex items-center gap-1"><Phone className="h-4 w-4" />{biz.phone}</span>}
          </div>
        </div>
      </div>

      {biz.description && <p className="text-zinc-600 mb-8 dark:text-zinc-400">{biz.description}</p>}

      {/* Packages section (if any active package) */}
      {packages.length > 0 && (
        <PackagesSection
          packages={packages}
          selectedPackageId={selectedPackage?.id || null}
          onSelect={choosePackage}
        />
      )}

      {/* Services section with auto/manual layout variants */}
      <ServicesSection
        services={biz.services || []}
        selectedServiceId={selectedService?.id || null}
        comboServices={comboServices}
        comboEnabled={!!settings.combo_enabled}
        layout={settings.services_layout || "auto"}
        onSelectSingle={chooseSingle}
        onToggleCombo={toggleComboService}
      />

      {/* Combo cart strip — visible only when combo has items */}
      {comboServices.length > 0 && (
        <ComboCart
          services={comboServices}
          totalDuration={selectionInfo?.totalDuration || 0}
          totalPrice={selectionInfo?.totalPrice || 0}
          onRemove={(s) => toggleComboService(s)}
          onClear={() => { setComboServices([]); setSelectedSlot(null); }}
        />
      )}

      {/* Combo staff picker — only when customer_choice mode and combo selected */}
      {(selectionMode === "combo" || selectionMode === "package") && settings.combo_staff_mode === "customer_choice" && staff.length > 0 && (
        <ComboStaffPicker
          staff={staff}
          value={comboStaffId}
          onChange={setComboStaffId}
        />
      )}

      {/* Single-service staff picker — only when service has ≥2 active eligible staff */}
      {selectionMode === "single" && (() => {
        const eligibleStaff = staff.filter((st) => (selectedService?.staff_ids || []).includes(st.id));
        if (eligibleStaff.length < 2) return null;
        return (
          <ComboStaffPicker
            staff={eligibleStaff}
            value={singleStaffId}
            onChange={setSingleStaffId}
          />
        );
      })()}

      {/* Date + Slots */}
      <AnimatePresence mode="wait" initial={false}>
        {hasSelection && (
          <motion.div
            key="slots-panel"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="mt-10"
          >
            <h2 className="font-display text-2xl tracking-tight font-medium mb-4">Scegli giorno</h2>
            {nextClosure && (
              <div className="rounded-2xl bg-rose-50 border border-rose-200 px-4 py-2.5 mb-3 text-xs text-rose-900 inline-flex items-center gap-2 dark:bg-rose-950/40 dark:border-rose-900/50 dark:text-rose-200" data-testid="next-closure-hint">
                <span aria-hidden>⚠️</span>
                Attenzione: prossima chiusura il <strong>{new Date(nextClosure.date + "T12:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long" })}</strong> ({nextClosure.label}).
              </div>
            )}
            <div className="flex gap-3 overflow-x-auto hide-scrollbar pb-3 snap-x">
              {days.map((d) => {
                const dy = ymd(d);
                const active = dy === ymd(selectedDate);
                const isClosed = !!overlay.holidays.find((h) => h.date === dy);
                const wkDow = (d.getDay() + 6) % 7;
                const hasOverride = !isClosed && overlay.overrides.some(
                  (o) => dy >= o.date_from && dy <= o.date_to && (o.active_dows || []).includes(wkDow),
                );
                return (
                  <button
                    key={dy}
                    onClick={() => setSelectedDate(d)}
                    className={`relative flex flex-col items-center justify-center min-w-[72px] h-[90px] rounded-2xl border snap-center transition-colors ${
                      active
                        ? "bg-zinc-900 text-white border-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 dark:border-zinc-100"
                        : isClosed
                          ? "bg-rose-50 border-rose-200 text-rose-700 hover:border-rose-300 dark:bg-rose-950/40 dark:border-rose-900/60 dark:text-rose-300"
                          : hasOverride
                            ? "bg-amber-50 border-amber-200 text-amber-900 hover:border-amber-300 dark:bg-amber-950/40 dark:border-amber-900/60 dark:text-amber-200"
                            : "border-zinc-200 bg-white hover:border-zinc-400 dark:bg-zinc-900 dark:border-zinc-800 dark:text-zinc-200 dark:hover:border-zinc-600"
                    }`}
                    data-testid={`date-${dy}`}
                    title={isClosed ? "Chiuso in questa data" : hasOverride ? "Orario speciale" : ""}
                  >
                    <span className="text-[10px] uppercase tracking-widest opacity-70">{d.toLocaleDateString("it-IT", { weekday: "short" })}</span>
                    <span className="font-display text-2xl">{d.getDate()}</span>
                    <span className="text-[10px] opacity-70">{d.toLocaleDateString("it-IT", { month: "short" })}</span>
                    {isClosed && !active && <span className="absolute top-1 right-1 text-[8px] font-bold uppercase tracking-wider">Chiuso</span>}
                  </button>
                );
              })}
            </div>

            {selectedHoliday && (
              <div className="rounded-2xl bg-rose-50 border border-rose-200 px-4 py-3 mb-3 dark:bg-rose-950/40 dark:border-rose-900/50" data-testid="selected-day-closed">
                <div className="text-sm font-semibold text-rose-900 dark:text-rose-200">L'attività è chiusa il {new Date(selectedYmd + "T12:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long" })}</div>
                <div className="text-xs text-rose-800 mt-0.5 dark:text-rose-300">{selectedHoliday.label}. Scegli un altro giorno o iscriviti alla lista d'attesa per essere avvisato.</div>
              </div>
            )}
            {selectedOverride && (
              <div className="rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3 mb-3 text-xs text-amber-900 dark:bg-amber-950/40 dark:border-amber-900/50 dark:text-amber-200" data-testid="selected-day-override">
                <strong>Orario speciale</strong> oggi: {selectedOverride.label} · apertura {Math.floor(selectedOverride.open_min / 60).toString().padStart(2, "0")}:{(selectedOverride.open_min % 60).toString().padStart(2, "0")} – chiusura {Math.floor(selectedOverride.close_min / 60).toString().padStart(2, "0")}:{(selectedOverride.close_min % 60).toString().padStart(2, "0")}.
              </div>
            )}

            <h2 className="font-display text-2xl tracking-tight font-medium mb-4 mt-6">Orari disponibili</h2>
            {slotLoading ? (
              <div className="text-sm text-zinc-500 dark:text-zinc-400">Calcolo…</div>
            ) : slots.length === 0 ? (
              <div className="card-soft p-6 text-center" data-testid="no-slots-cta">
                <Hourglass className="h-9 w-9 mx-auto text-zinc-300 mb-2" />
                <div className="font-display text-lg font-medium">Tutto prenotato in questa data</div>
                <p className="text-sm text-zinc-500 mt-1 max-w-md mx-auto dark:text-zinc-400">
                  Iscriviti alla lista d'attesa: ti avviseremo via email appena si libera uno slot
                  che combacia con le tue preferenze.
                </p>
                <button
                  onClick={() => setWaitlistOpen(true)}
                  className="btn-accent mt-4 inline-flex items-center"
                  data-testid="join-waitlist-cta"
                >
                  <Hourglass className="h-4 w-4 mr-2" />
                  Iscriviti alla lista d'attesa
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {slots.some((s) => s.suggested) && (
                  <div className="flex items-center gap-2 text-[11px] text-zinc-500 dark:text-zinc-400">
                    <span className="inline-block h-2 w-2 rounded-full bg-amber-500" />
                    <span>Gli orari con la stella sono <strong className="text-zinc-700 dark:text-zinc-300">suggeriti</strong>: riducono i tempi morti in agenda.</span>
                  </div>
                )}
                {slots.some((s) => (s.free_staff_count || 0) >= 2) && (
                  <div className="flex items-center gap-2 text-[11px] text-zinc-500 dark:text-zinc-400" data-testid="capacity-legend">
                    <Users className="h-3 w-3 text-blue-600" />
                    <span>Il numero accanto all'orario indica <strong className="text-zinc-700 dark:text-zinc-300">quanti operatori</strong> sono ancora liberi in quello slot.</span>
                  </div>
                )}
                <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 gap-3">
                  {slots.map((s) => {
                    const iso = s.start;
                    const active = iso === selectedSlot;
                    const cap = s.free_staff_count || 0;
                    return (
                      <button
                        key={iso}
                        onClick={() => setSelectedSlot(iso)}
                        className={`relative h-12 rounded-xl border font-medium transition-all ${active ? "bg-blue-600 text-white border-blue-600" : s.suggested ? "border-amber-400 bg-amber-50 hover:border-amber-500 text-zinc-900 dark:bg-amber-950/40 dark:border-amber-700 dark:text-amber-200" : "border-zinc-200 hover:border-zinc-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:border-zinc-500"}`}
                        data-testid={`slot-${iso}`}
                        data-suggested={s.suggested ? "1" : "0"}
                        data-capacity={cap || ""}
                        title={s.suggested ? "Suggerito: riduce i tempi morti" : (cap >= 2 ? `${cap} operatori disponibili` : "")}
                      >
                        {hm(iso)}
                        {s.suggested && !active && (
                          <span className="absolute -top-1.5 -right-1.5 text-[10px] text-amber-600" aria-hidden>★</span>
                        )}
                        {cap >= 2 && !active && (
                          <span className="absolute -bottom-1.5 -right-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full bg-blue-600 text-white shadow-sm" data-testid={`slot-capacity-${iso}`} aria-label={`${cap} operatori disponibili`}>
                            ×{cap}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
                {/* Always allow joining waitlist even when slots are present (e.g. customer wants earlier date) */}
                <div className="text-center pt-2">
                  <button
                    onClick={() => setWaitlistOpen(true)}
                    className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
                    data-testid="join-waitlist-link"
                  >
                    <Hourglass className="h-3 w-3" /> Non vedi l'orario giusto? Iscriviti alla lista d'attesa
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Confirm sticky */}
      <AnimatePresence mode="wait" initial={false}>
        {selectedSlot && (
          <motion.div
            key="confirm-bar"
            initial={{ y: 100 }}
            animate={{ y: 0 }}
            exit={{ y: 100 }}
            transition={{ type: "spring", damping: 20 }}
            className="fixed bottom-0 inset-x-0 z-40"
          >
            <div className="max-w-5xl mx-auto px-4 pb-4 md:pb-6">
              <div className="card-soft p-4 md:p-5 space-y-3">
                <label className="flex items-start gap-2 text-[11px] text-zinc-600 select-none cursor-pointer dark:text-zinc-400" data-testid="booking-privacy-label">
                  <input type="checkbox" checked={privacyOk} onChange={(e) => setPrivacyOk(e.target.checked)} className="mt-0.5 h-4 w-4 accent-blue-600" data-testid="booking-privacy-checkbox" />
                  <span>
                    Accetto l'<Link to={`/p/${id}/privacy`} target="_blank" className="text-blue-600 hover:underline">Informativa Privacy di {biz.name}</Link> ex art. 13 GDPR. I miei dati saranno trattati dall'attività per gestire la prenotazione.
                  </span>
                </label>
                <p className="text-[10px] text-zinc-400 -mt-1 dark:text-zinc-500">
                  Piattaforma tecnica fornita da Prenotoo (Responsabile del trattamento) — vedi <Link to="/dpa" target="_blank" className="hover:underline">DPA</Link>.
                </p>
                <div className="flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{selectionInfo?.title} · {biz.name}</div>
                    <div className="text-xs text-zinc-500 truncate dark:text-zinc-400">
                      {new Date(selectedSlot).toLocaleString("it-IT", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })} · €{selectionInfo?.totalPrice?.toFixed?.(2) ?? selectionInfo?.totalPrice}
                      {selectionMode !== "single" && <> · {selectionInfo?.totalDuration}min</>}
                    </div>
                  </div>
                  <button onClick={() => setSelectedSlot(null)} className="h-10 w-10 rounded-full bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center dark:bg-zinc-800 dark:hover:bg-zinc-700" data-testid="cancel-slot">
                    <X className="h-4 w-4" />
                  </button>
                  <button onClick={confirm} disabled={confirming || !privacyOk} className="btn-accent disabled:opacity-50" data-testid="confirm-booking">
                    {confirming ? "Attendere…" : (
                      selectionInfo?.hasDeposit
                        ? (<><CreditCard className="h-4 w-4 mr-2" />Paga caparra</>)
                        : (<><Check className="h-4 w-4 mr-2" />Conferma</>)
                    )}
                  </button>
                </div>
              </div>
              {msg && <div className="mt-2 text-sm text-center text-zinc-700 dark:text-zinc-300" data-testid="booking-message">{msg}</div>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <GuestBookingModal
        open={guestModalOpen}
        onClose={() => setGuestModalOpen(false)}
        onSubmit={submitGuestBooking}
        loading={confirming}
        selectionInfo={selectionInfo}
        selectedSlot={selectedSlot}
        bizName={biz.name}
        bizId={id}
      />
      <GuestSuccessModal
        data={guestSuccess}
        onClose={() => setGuestSuccess(null)}
        bizName={biz.name}
      />
      <WaitlistJoinModal
        open={waitlistOpen}
        onClose={() => setWaitlistOpen(false)}
        onSuccess={() => { setWaitlistOpen(false); setWaitlistOk(true); setTimeout(() => setWaitlistOk(false), 6000); }}
        bizId={id}
        bizName={biz.name}
        services={biz.services || []}
        defaultServiceId={selectedService?.id}
        defaultDate={ymd(selectedDate)}
        prefillName={user?.name || ""}
        prefillEmail={user?.email || ""}
        prefillPhone={user?.phone || ""}
      />
      {waitlistOk && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[60] max-w-sm w-[92%] md:w-[440px]" data-testid="waitlist-ok-toast">
          <div className="bg-emerald-600 text-white text-sm px-4 py-3 rounded-2xl shadow-lg mb-2">
            ✓ Iscritto alla lista d'attesa. Ti scriviamo appena si libera uno slot.
          </div>
          {/* Now is the moment to ask for push: highest perceived value */}
          <PushOptInBanner
            force
            hint="Per essere il primo a saperlo: attiva le notifiche push e ricevi un avviso istantaneo (anche prima dell'email)."
          />
        </div>
      )}
    </div>
  );
}

// ---------- Guest Booking Modal ----------
function GuestBookingModal({ open, onClose, onSubmit, loading, selectionInfo, selectedSlot, bizName, bizId }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [privacyGuest, setPrivacyGuest] = useState(false);
  const [localErr, setLocalErr] = useState("");

  if (!open) return null;

  function handleSubmit(e) {
    e.preventDefault();
    setLocalErr("");
    if (!privacyGuest) {
      setLocalErr("Devi accettare l'Informativa Privacy per prenotare.");
      return;
    }
    onSubmit({ name: name.trim(), email: email.trim().toLowerCase(), phone: phone.trim(), notes: notes.trim() });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="guest-modal" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl dark:bg-zinc-900" onClick={(e) => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-600">Prenota come ospite</div>
              <h2 className="font-display text-2xl font-medium tracking-tight mt-1">I tuoi dati</h2>
            </div>
            <button onClick={onClose} className="h-9 w-9 rounded-full bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center dark:bg-zinc-800 dark:hover:bg-zinc-700" data-testid="guest-modal-close">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="rounded-2xl bg-zinc-50 p-4 mb-5 text-sm dark:bg-zinc-800">
            <div className="font-medium">{selectionInfo?.title} · {bizName}</div>
            <div className="text-xs text-zinc-600 mt-1 dark:text-zinc-400">
              {selectedSlot && new Date(selectedSlot).toLocaleString("it-IT", { weekday: "long", day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit" })}
              {" · "}€{selectionInfo?.totalPrice?.toFixed?.(2) ?? selectionInfo?.totalPrice}
              {selectionInfo?.totalDuration ? ` · ${selectionInfo.totalDuration}min` : ""}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3" data-testid="guest-form">
            <div>
              <label className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Nome e cognome *</label>
              <input type="text" required minLength={2} maxLength={120} value={name} onChange={(e) => setName(e.target.value)} className="input-soft mt-1.5" data-testid="guest-name" autoFocus />
            </div>
            <div>
              <label className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Email *</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input-soft mt-1.5" data-testid="guest-email" />
              <div className="text-[11px] text-zinc-500 mt-1 dark:text-zinc-400">Riceverai conferma e promemoria qui.</div>
            </div>
            <div>
              <label className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Telefono *</label>
              <input type="tel" required minLength={5} value={phone} onChange={(e) => setPhone(e.target.value)} className="input-soft mt-1.5" data-testid="guest-phone" placeholder="+39 ..." />
            </div>
            <div>
              <label className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Note (facoltative)</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="input-soft mt-1.5 resize-none" data-testid="guest-notes" placeholder="Preferenze, richieste speciali…" maxLength={500} />
              <p className="text-[10px] text-zinc-400 mt-1 dark:text-zinc-500">
                Non inserire dati sanitari sensibili (diagnosi, terapie). Per quelli usate canali dedicati con il professionista.
              </p>
            </div>

            <label className="flex items-start gap-2 text-[11px] text-zinc-600 select-none cursor-pointer mt-2 dark:text-zinc-400" data-testid="guest-privacy-label">
              <input type="checkbox" checked={privacyGuest} onChange={(e) => setPrivacyGuest(e.target.checked)} className="mt-0.5 h-4 w-4 accent-blue-600" data-testid="guest-privacy-checkbox" />
              <span>
                Accetto l'<Link to={`/p/${bizId}/privacy`} target="_blank" className="text-blue-600 hover:underline">Informativa Privacy di {bizName}</Link> ex art. 13 GDPR. I miei dati saranno trattati dall'attività per gestire la prenotazione.
              </span>
            </label>
            <p className="text-[10px] text-zinc-400 dark:text-zinc-500">
              Piattaforma tecnica fornita da Prenotoo (Responsabile del trattamento) — vedi <Link to="/dpa" target="_blank" className="hover:underline">DPA</Link>.
            </p>

            {localErr && <div className="text-sm text-red-600" data-testid="guest-form-error">{localErr}</div>}

            <button type="submit" disabled={loading || !privacyGuest} className="btn-accent w-full disabled:opacity-50 mt-2" data-testid="guest-submit">
              {loading ? "Prenoto…" : (<><Check className="h-4 w-4 mr-2" />Conferma prenotazione</>)}
            </button>

            <div className="text-center text-xs text-zinc-500 pt-2 border-t border-zinc-100 mt-3 dark:text-zinc-400 dark:border-zinc-800">
              Hai già un account?{" "}
              <Link to={`/login?return=/book/${bizId}`} className="text-blue-600 hover:underline font-medium" data-testid="guest-login-link">
                Accedi
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ---------- Guest Success Modal: offer to create account ----------
function GuestSuccessModal({ data, onClose, bizName }) {
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  if (!data) return null;

  async function createAccount(e) {
    e.preventDefault();
    setErr("");
    if (password.length < 8) {
      setErr("La password deve avere almeno 8 caratteri.");
      return;
    }
    setCreating(true);
    try {
      const { data: reg } = await api.post("/auth/register", {
        email: data.email,
        password,
        name: data.name,
        role: "customer",
        phone: data.phone,
        privacy_accepted: true,
      });
      if (reg?.token) {
        localStorage.setItem("token", reg.token);
      }
      // Claim guest bookings by email
      try {
        await api.post("/bookings/claim-guest");
      } catch { /* non-blocking */ }
      nav("/me");
      // Force full reload so AuthContext picks up the new user
      window.location.reload();
    } catch (ex) {
      setErr(formatErr(ex.response?.data?.detail) || "Errore durante la registrazione.");
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="guest-success-modal">
      <div className="bg-white rounded-3xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl dark:bg-zinc-900">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-12 w-12 rounded-2xl bg-emerald-100 text-emerald-600 flex items-center justify-center dark:bg-emerald-950/40">
              <Check className="h-6 w-6" />
            </div>
            <div>
              <div className="text-xs font-semibold tracking-[0.2em] uppercase text-emerald-600">Confermata</div>
              <h2 className="font-display text-xl font-medium tracking-tight">Prenotazione creata</h2>
            </div>
          </div>

          <div className="rounded-2xl bg-zinc-50 p-4 mb-5 text-sm dark:bg-zinc-800">
            <div className="font-medium">{data.serviceName} · {bizName}</div>
            <div className="text-xs text-zinc-600 mt-1 dark:text-zinc-400">
              {new Date(data.startAt).toLocaleString("it-IT", { weekday: "long", day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit" })}
            </div>
            <div className="text-xs text-zinc-600 mt-2 dark:text-zinc-400">Riepilogo inviato a <strong>{data.email}</strong></div>
          </div>

          {!showCreate ? (
            <>
              <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 mb-4 dark:border-blue-800/50 dark:bg-blue-950/40">
                <div className="font-medium text-sm text-blue-900 dark:text-blue-200">Crea il tuo account gratuito</div>
                <p className="text-xs text-blue-800 mt-1 dark:text-blue-300">Potrai vedere lo storico, annullare e gestire le prossime prenotazioni in un click.</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setShowCreate(true)} className="btn-accent flex-1" data-testid="offer-create-account">
                  Sì, crea account
                </button>
                <button onClick={onClose} className="btn-secondary" data-testid="offer-skip">
                  No, grazie
                </button>
              </div>
            </>
          ) : (
            <form onSubmit={createAccount} className="space-y-3" data-testid="create-account-form">
              <div className="text-xs text-zinc-600 dark:text-zinc-400">
                Creiamo l'account con <strong>{data.email}</strong>. Scegli una password:
              </div>
              <input
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-soft"
                placeholder="Almeno 8 caratteri"
                data-testid="create-account-password"
                autoFocus
              />
              {err && <div className="text-sm text-red-600" data-testid="create-account-error">{err}</div>}
              <div className="flex gap-2">
                <button type="submit" disabled={creating} className="btn-accent flex-1 disabled:opacity-50" data-testid="create-account-submit">
                  {creating ? "Creo…" : "Crea account"}
                </button>
                <button type="button" onClick={onClose} className="btn-secondary">
                  Salta
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}


// ---------- Waitlist Join Modal ----------
const WAITLIST_DOWS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
function HHMMtoMin(s) { if (!s) return null; const [h, m] = s.split(":").map((x) => parseInt(x, 10)); if (Number.isNaN(h)) return null; return h * 60 + (m || 0); }

function WaitlistJoinModal({ open, onClose, onSuccess, bizId, bizName, services, defaultServiceId, defaultDate, prefillName, prefillEmail, prefillPhone }) {
  const [serviceId, setServiceId] = useState(defaultServiceId || services[0]?.id || "");
  const [name, setName] = useState(prefillName || "");
  const [email, setEmail] = useState(prefillEmail || "");
  const [phone, setPhone] = useState(prefillPhone || "");
  const [dateFrom, setDateFrom] = useState(defaultDate || "");
  const [dateTo, setDateTo] = useState("");
  const [dows, setDows] = useState([]);
  const [timeFrom, setTimeFrom] = useState("");
  const [timeTo, setTimeTo] = useState("");
  const [notes, setNotes] = useState("");
  const [privacy, setPrivacy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      setServiceId(defaultServiceId || services[0]?.id || "");
      setName(prefillName || "");
      setEmail(prefillEmail || "");
      setPhone(prefillPhone || "");
      setDateFrom(defaultDate || "");
      setDateTo("");
      setDows([]);
      setTimeFrom(""); setTimeTo("");
      setNotes("");
      setPrivacy(false);
      setErr("");
    }
  }, [open, defaultServiceId, defaultDate, services, prefillName, prefillEmail, prefillPhone]);

  if (!open) return null;

  function toggleDow(d) { setDows((prev) => prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort()); }

  async function submit(e) {
    e.preventDefault();
    setErr("");
    if (!privacy) return setErr("Devi accettare l'Informativa Privacy.");
    if (!name.trim() || name.trim().length < 2) return setErr("Inserisci nome e cognome.");
    if (!email.trim()) return setErr("Inserisci la tua email.");
    if (!phone.trim() || phone.trim().length < 5) return setErr("Inserisci un numero di telefono valido.");
    setBusy(true);
    try {
      await api.post(`/businesses/${bizId}/waitlist/join`, {
        service_id: serviceId,
        customer_name: name.trim(),
        customer_email: email.trim().toLowerCase(),
        customer_phone: phone.trim(),
        preferred_date_from: dateFrom || null,
        preferred_date_to: dateTo || null,
        preferred_dows: dows,
        preferred_time_from_min: HHMMtoMin(timeFrom),
        preferred_time_to_min: HHMMtoMin(timeTo),
        notes: notes.trim() || "",
        privacy_accepted: true,
      });
      onSuccess();
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || "Errore");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="waitlist-join-modal">
      <div className="bg-white rounded-3xl w-full max-w-md max-h-[92vh] overflow-y-auto shadow-2xl dark:bg-zinc-900" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={submit} className="p-6 space-y-3">
          <div className="flex items-start justify-between mb-1">
            <div>
              <div className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-600">Lista d'attesa</div>
              <h2 className="font-display text-2xl font-medium tracking-tight mt-1">Avvisami quando si libera</h2>
              <p className="text-xs text-zinc-500 mt-1 dark:text-zinc-400">{bizName}</p>
            </div>
            <button type="button" onClick={onClose} className="h-9 w-9 rounded-full bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center dark:bg-zinc-800 dark:hover:bg-zinc-700" data-testid="waitlist-join-close">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div>
            <label className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Servizio *</label>
            <select required value={serviceId} onChange={(e) => setServiceId(e.target.value)} className="input-soft mt-1.5" data-testid="waitlist-join-service">
              {services.map((s) => <option key={s.id} value={s.id}>{s.name} · {s.duration_min}min · €{s.price}</option>)}
            </select>
          </div>

          <div>
            <label className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Nome e cognome *</label>
            <input required minLength={2} value={name} onChange={(e) => setName(e.target.value)} className="input-soft mt-1.5" data-testid="waitlist-join-name" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Email *</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input-soft mt-1.5" data-testid="waitlist-join-email" />
            </div>
            <div>
              <label className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Telefono *</label>
              <input required minLength={5} value={phone} onChange={(e) => setPhone(e.target.value)} className="input-soft mt-1.5" data-testid="waitlist-join-phone" placeholder="+39 …" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Da</label>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="input-soft mt-1.5" />
            </div>
            <div>
              <label className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">A</label>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="input-soft mt-1.5" />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 mb-1.5 block dark:text-zinc-400">Giorni preferiti (opz.)</label>
            <div className="flex flex-wrap gap-1.5">
              {WAITLIST_DOWS.map((d, i) => (
                <button type="button" key={i} onClick={() => toggleDow(i)} className={`h-9 px-3 rounded-full text-xs font-medium border transition ${dows.includes(i) ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"}`}>
                  {d}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Dalle ore</label>
              <input type="time" value={timeFrom} onChange={(e) => setTimeFrom(e.target.value)} className="input-soft mt-1.5" />
            </div>
            <div>
              <label className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Alle ore</label>
              <input type="time" value={timeTo} onChange={(e) => setTimeTo(e.target.value)} className="input-soft mt-1.5" />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Note (opz.)</label>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="input-soft mt-1.5 resize-none" placeholder="Es. preferisco mattino, urgenza, ecc." maxLength={400} />
            <p className="text-[10px] text-zinc-400 mt-1 dark:text-zinc-500">Non includere informazioni sanitarie sensibili.</p>
          </div>

          <label className="flex items-start gap-2 text-[11px] text-zinc-600 select-none cursor-pointer mt-1 dark:text-zinc-400" data-testid="waitlist-join-privacy-label">
            <input type="checkbox" checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} className="mt-0.5 h-4 w-4 accent-blue-600" data-testid="waitlist-join-privacy" />
            <span>
              Accetto l'<Link to={`/p/${bizId}/privacy`} target="_blank" className="text-blue-600 hover:underline">Informativa Privacy di {bizName}</Link> ex art. 13 GDPR. I miei dati saranno trattati dall'attività solo per contattarmi quando si libera uno slot.
            </span>
          </label>
          <p className="text-[10px] text-zinc-400 dark:text-zinc-500">
            Piattaforma tecnica fornita da Prenotoo (Responsabile del trattamento) — vedi <Link to="/dpa" target="_blank" className="hover:underline">DPA</Link>.
          </p>

          {err && <div className="text-sm text-red-600" data-testid="waitlist-join-error">{err}</div>}

          <button type="submit" disabled={busy || !privacy} className="btn-accent w-full disabled:opacity-50" data-testid="waitlist-join-submit">
            {busy ? "Iscrivo…" : (<><Hourglass className="h-4 w-4 mr-2" />Iscriviti alla lista d'attesa</>)}
          </button>
        </form>
      </div>
    </div>
  );
}


// ============== Sub-components for service display + combo + packages ==============

function ServiceMeta({ s }) {
  return (
    <div className="flex gap-4 text-sm text-zinc-500 mt-1 flex-wrap dark:text-zinc-400">
      <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{s.duration_min} min</span>
      <span className="inline-flex items-center gap-1"><Euro className="h-3.5 w-3.5" />{s.price}</span>
      {(s.max_concurrent || 1) > 1 && (
        <span className="inline-flex items-center gap-1 text-blue-600 font-medium">
          <Users className="h-3.5 w-3.5" />Gruppo · {s.max_concurrent} posti
        </span>
      )}
      {s.deposit_type && s.deposit_type !== "none" && Number(s.deposit_value) > 0 && (
        <span className="inline-flex items-center gap-1 text-amber-700 font-medium dark:text-amber-300">
          <CreditCard className="h-3.5 w-3.5" />
          Caparra {s.deposit_type === "percent" ? `${s.deposit_value}%` : `€${s.deposit_value}`}
        </span>
      )}
    </div>
  );
}

function ServiceCard({ s, active, inCombo, comboEnabled, onSelect, onToggleCombo }) {
  return (
    <div className={`relative rounded-2xl border bg-white p-5 transition dark:bg-zinc-900 ${active ? "border-zinc-900 ring-2 ring-zinc-900 dark:border-zinc-100 dark:ring-zinc-100" : inCombo ? "border-blue-500 ring-2 ring-blue-200 dark:ring-blue-900/60" : "border-zinc-200 hover:border-zinc-400 dark:border-zinc-800 dark:hover:border-zinc-600"}`}>
      <button
        type="button"
        onClick={() => onSelect(s)}
        className="w-full text-left"
        data-testid={`service-${s.id}`}
      >
        <div className="font-medium text-lg">{s.name}</div>
        <ServiceMeta s={s} />
      </button>
      {comboEnabled && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onToggleCombo(s); }}
          className={`absolute top-3 right-3 inline-flex items-center gap-1 h-8 px-3 rounded-full text-xs font-medium transition ${inCombo ? "bg-blue-600 text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700"}`}
          data-testid={`service-combo-${s.id}`}
          title={inCombo ? "Rimuovi dalla combo" : "Aggiungi alla combo"}
        >
          {inCombo ? <><Check className="h-3 w-3" />In combo</> : <><Plus className="h-3 w-3" />Combo</>}
        </button>
      )}
    </div>
  );
}

function ServicesSection({ services, selectedServiceId, comboServices, comboEnabled, layout, onSelectSingle, onToggleCombo }) {
  const [search, setSearch] = useState("");
  const filtered = useMemo(() => {
    if (!search.trim()) return services;
    const q = search.toLowerCase();
    return services.filter((s) =>
      (s.name || "").toLowerCase().includes(q) ||
      (s.description || "").toLowerCase().includes(q) ||
      (s.category || "").toLowerCase().includes(q),
    );
  }, [services, search]);

  // Resolve effective layout
  const effective = useMemo(() => {
    if (layout && layout !== "auto") return layout;
    const n = services.length;
    if (n <= 8) return "grid";
    if (n <= 30) return "list";
    return "search";
  }, [layout, services.length]);

  // Group by category for "list" mode
  const groups = useMemo(() => {
    if (effective !== "list") return null;
    const m = new Map();
    for (const s of filtered) {
      const k = (s.category || "").trim() || "Altri servizi";
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(s);
    }
    // Sort: categories with services come first, "Altri servizi" last
    return Array.from(m.entries()).sort(([a], [b]) => {
      if (a === "Altri servizi") return 1;
      if (b === "Altri servizi") return -1;
      return a.localeCompare(b);
    });
  }, [filtered, effective]);

  const inComboIds = useMemo(() => new Set(comboServices.map((x) => x.id)), [comboServices]);

  return (
    <div data-testid="services-section">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h2 className="font-display text-2xl tracking-tight font-medium">Servizi <span className="text-base font-normal text-zinc-400 dark:text-zinc-500">({services.length})</span></h2>
        {effective === "search" && (
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 dark:text-zinc-500" />
            <input
              type="search"
              placeholder="Cerca servizio…"
              className="input-soft pl-10"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="service-search"
            />
          </div>
        )}
      </div>

      {comboEnabled && (
        <div className="rounded-2xl bg-blue-50 border border-blue-200 px-4 py-3 mb-4 text-xs text-blue-900 dark:bg-blue-950/40 dark:border-blue-900/50 dark:text-blue-200" data-testid="combo-hint">
          <strong>Vuoi prenotare più servizi consecutivi?</strong> Tocca <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-white border border-blue-200 dark:bg-zinc-900 dark:border-blue-900/60"><Plus className="h-3 w-3" />Combo</span> sui servizi che vuoi unire (max 5). Il sistema troverà uno slot in cui entrano tutti.
        </div>
      )}

      {effective === "grid" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map((s) => (
            <ServiceCard
              key={s.id}
              s={s}
              active={selectedServiceId === s.id}
              inCombo={inComboIds.has(s.id)}
              comboEnabled={comboEnabled}
              onSelect={onSelectSingle}
              onToggleCombo={onToggleCombo}
            />
          ))}
        </div>
      )}

      {effective === "list" && groups && (
        <div className="space-y-6">
          {groups.map(([cat, list]) => (
            <div key={cat} data-testid={`service-group-${cat.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="text-xs font-semibold tracking-[0.18em] uppercase text-zinc-500 mb-2 dark:text-zinc-400">{cat}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {list.map((s) => (
                  <ServiceCard
                    key={s.id}
                    s={s}
                    active={selectedServiceId === s.id}
                    inCombo={inComboIds.has(s.id)}
                    comboEnabled={comboEnabled}
                    onSelect={onSelectSingle}
                    onToggleCombo={onToggleCombo}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {effective === "search" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.length === 0 && (
            <div className="text-sm text-zinc-500 col-span-2 text-center py-6 dark:text-zinc-400">Nessun servizio trovato.</div>
          )}
          {filtered.map((s) => (
            <ServiceCard
              key={s.id}
              s={s}
              active={selectedServiceId === s.id}
              inCombo={inComboIds.has(s.id)}
              comboEnabled={comboEnabled}
              onSelect={onSelectSingle}
              onToggleCombo={onToggleCombo}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PackagesSection({ packages, selectedPackageId, onSelect }) {
  return (
    <div className="mb-8" data-testid="packages-section">
      <div className="flex items-center gap-2 mb-3">
        <Package className="h-5 w-5 text-blue-600" />
        <h2 className="font-display text-2xl tracking-tight font-medium">Pacchetti</h2>
      </div>
      <p className="text-sm text-zinc-500 mb-4 dark:text-zinc-400">Più servizi in un'unica prenotazione, a prezzo speciale.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {packages.map((p) => {
          const active = p.id === selectedPackageId;
          const hasDiscount = p.final_price < p.raw_price;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => onSelect(p)}
              className={`text-left rounded-2xl border bg-white p-5 transition dark:bg-zinc-900 ${active ? "border-zinc-900 ring-2 ring-zinc-900 dark:border-zinc-100 dark:ring-zinc-100" : "border-zinc-200 hover:border-zinc-400 dark:border-zinc-800 dark:hover:border-zinc-600"}`}
              data-testid={`package-${p.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="font-medium text-lg">{p.name}</div>
                  {p.description && <div className="text-sm text-zinc-600 mt-0.5 dark:text-zinc-400">{p.description}</div>}
                </div>
                {hasDiscount && (
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300">
                    -{Math.round((1 - p.final_price / p.raw_price) * 100)}%
                  </span>
                )}
              </div>
              <div className="text-xs text-zinc-500 mt-2 inline-flex items-center gap-1 dark:text-zinc-400">
                <Tag className="h-3 w-3" />
                {(p.services || []).map((s) => s.name).join(" → ")}
              </div>
              <div className="flex items-center gap-3 mt-3 text-sm">
                <span className="inline-flex items-center gap-1 text-zinc-700 dark:text-zinc-300"><Clock className="h-3.5 w-3.5" />{p.total_duration_min}min</span>
                <span className="inline-flex items-center gap-1 font-semibold">
                  {hasDiscount ? <>
                    <span className="line-through text-zinc-400 dark:text-zinc-500">€{p.raw_price.toFixed(2)}</span>
                    <span className="text-emerald-700 dark:text-emerald-400">€{p.final_price.toFixed(2)}</span>
                  </> : <>€{p.final_price.toFixed(2)}</>}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ComboCart({ services, totalDuration, totalPrice, onRemove, onClear }) {
  return (
    <div className="rounded-2xl bg-zinc-900 text-white p-4 md:p-5 mt-6 mb-2 shadow-lg" data-testid="combo-cart">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-blue-300 mb-2">La tua combo · {services.length} servizi</div>
          <div className="flex flex-wrap gap-2">
            {services.map((s, i) => (
              <span key={s.id} className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/15 rounded-full pl-3 pr-1 py-1 text-sm" data-testid={`combo-cart-item-${s.id}`}>
                <span className="opacity-60 text-xs">{i + 1}.</span>
                <span>{s.name}</span>
                <button onClick={() => onRemove(s)} className="h-5 w-5 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center" aria-label="Rimuovi">
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        </div>
        <button onClick={onClear} className="text-xs text-white/70 hover:text-white whitespace-nowrap" data-testid="combo-cart-clear">Svuota</button>
      </div>
      <div className="border-t border-white/10 mt-3 pt-3 flex items-center justify-between text-sm">
        <span className="text-white/70">Totale: <strong className="text-white">{totalDuration} min</strong></span>
        <span className="text-lg font-semibold">€{totalPrice.toFixed(2)}</span>
      </div>
    </div>
  );
}

function ComboStaffPicker({ staff, value, onChange }) {
  return (
    <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 mt-3 dark:bg-amber-950/30 dark:border-amber-900/50" data-testid="combo-staff-picker">
      <div className="text-xs font-semibold tracking-[0.18em] uppercase text-amber-900 mb-2 dark:text-amber-200">Scegli operatore</div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => onChange(null)}
          className={`h-9 px-3 rounded-full text-sm font-medium border transition ${!value ? "bg-zinc-900 text-white border-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 dark:border-zinc-100" : "bg-white text-zinc-700 border-zinc-200 dark:bg-zinc-900 dark:text-zinc-300 dark:border-zinc-700"}`}
          data-testid="combo-staff-any"
        >
          Indifferente
        </button>
        {staff.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => onChange(s.id)}
            className={`h-9 px-3 rounded-full text-sm font-medium border transition inline-flex items-center gap-2 ${value === s.id ? "bg-zinc-900 text-white border-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 dark:border-zinc-100" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400 dark:bg-zinc-900 dark:text-zinc-300 dark:border-zinc-700 dark:hover:border-zinc-500"}`}
            data-testid={`combo-staff-${s.id}`}
          >
            <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: s.color || "#2563EB" }} />
            {s.name}
          </button>
        ))}
      </div>
    </div>
  );
}
